/* eslint-disable import/prefer-default-export */
import { ref } from "vue";
import { defineStore } from "pinia";
import { i18n } from "@/packages/i18n";

import useLocalStorage from "@/composables/storage/useLocalStorage.js";
import filters from "@/composables/store/filters";
import useFormValidation from "@/composables/validations/useFormValidation";
import useGlobalActions from "@/composables/store/globalActions";

const { bindLocalStorage } = useLocalStorage();
const { updateErrorFieldByKey } = useGlobalActions();
const {
  validateCurpField,
  validateCpfField,
  validateSaidField,
  validateCcField,
  validateDniField,
  validateCiField,
  validateTicField,
} = useFormValidation();
const { formHasErrors, getErrorByField } = filters();
const { t } = i18n.global;

const formObject = ref({
  curp: {
    title: t("PageTitle.headline.dLocalIdentityCurp"),
    label: t("FormInputField.label.curpNumber"),
    placeholder: t("FormInputField.placeholder.curpNumber"),
    value: null,
    required: true,
    error: null,
    validator: validateCurpField,
  },
  said: {
    title: t("PageTitle.headline.dLocalIdentitySaid"),
    label: t("FormInputField.label.saidNumber"),
    placeholder: t("FormInputField.placeholder.saidNumber"),
    value: null,
    required: true,
    error: null,
    validator: validateSaidField,
  },
  cpf: {
    title: t("PageTitle.headline.dLocalIdentityCpf"),
    label: t("FormInputField.label.cpfNumber"),
    placeholder: t("FormInputField.placeholder.cpfNumber"),
    value: null,
    required: true,
    error: null,
    validator: validateCpfField,
  },
  cc: {
    title: t("PageTitle.headline.dLocalIdentityCC"),
    label: t("FormInputField.label.ccNumber"),
    placeholder: t("FormInputField.placeholder.ccNumber"),
    value: null,
    required: true,
    error: null,
    validator: validateCcField,
  },
  dni: {
    title: t("PageTitle.headline.dLocalIdentityDni"),
    label: t("FormInputField.label.dniNumber"),
    placeholder: t("FormInputField.placeholder.dniNumber"),
    value: null,
    required: true,
    error: null,
    validator: validateDniField,
  },
  ci: {
    title: t("PageTitle.headline.dLocalIdentityCi"),
    label: t("FormInputField.label.ciNumber"),
    placeholder: t("FormInputField.placeholder.ciNumber"),
    value: null,
    required: true,
    error: null,
    validator: validateCiField,
  },
  tic: {
    title: t("PageTitle.headline.dlocalIdentityTic"),
    label: t("FormInputField.label.ticNumber"),
    placeholder: t("FormInputField.placeholder.ticNumber"),
    value: null,
    required: true,
    error: null,
    validator: validateTicField,
  },
});

export const useDlocalIdentityStore = defineStore({
  id: "dlocalIdentityStore",
  state: () => ({
    form: formObject,
  }),
  getters: {
    formHasErrors: (store) => formHasErrors(store),
    getErrorByField: (store) => (field) => getErrorByField(store)(field),
  },
  actions: {
    updateValue(field, val) {
      if (Object.getOwnPropertyDescriptor(this.form[field], "validator")) {
        const { validator } = this.form[field];

        this.form[field].error = validator(field, val);
      }
      this.form[field].value = val;
      bindLocalStorage(this.$id, this.form);
    },

    /**
     * NOTE:
     *  KEY or KEYS in this file is referred to keys of formObject. For example, curp, said etc.
     */

    // Update required and error attributes for all the fields that is not required for the selected PSP
    updateRequiredAndErrorFieldForNotSelectedKeys(selectedField) {
      if (
        !selectedField ||
        !Object.getOwnPropertyDescriptor(this.form, selectedField) ||
        !Object.getOwnPropertyDescriptor(this.form[selectedField], "required")
      ) {
        return;
      }

      const clone = { ...this.form };
      delete clone[selectedField];

      Object.keys(clone).forEach((key) => {
        this.form[key].required = false;
        this.form[key].error = "";
      });
    },

    updateErrorFieldByKey(field, errorMessage) {
      updateErrorFieldByKey(this.form, field, errorMessage);
    },
  },
});
